// Import auth types for internal use
import type { UserRole, User, UserSettings } from './auth';

// Re-export auth types from auth.ts
export * from './auth';

// Legacy User interface - deprecated, use the one from auth.ts
// Keeping for backward compatibility during migration
/** @deprecated Use User from './auth' instead */
export interface LegacyUser {
  id: string;
  username?: string; // Legacy field, use displayName instead
  displayName?: string;
  role: UserRole;
  name: string;
  email?: string;
  createdAt?: string;
  settings?: UserSettings;
  emailVerified?: boolean;
}

// History types
export type HistoryAction = 'create' | 'update' | 'delete';
export type HistoryEntityType = 'project' | 'version' | 'screen' | 'event' | 'comment';

export interface HistoryRecord {
  id: string;
  projectId: string;
  action: HistoryAction;
  entityType: HistoryEntityType;
  entityId: string;
  entityName?: string;
  userId: string;
  userName: string;
  userRole?: string;
  // userAvatar удален - аватар загружается динамически по userId из коллекции users
  oldValue?: any;
  newValue?: any;
  timestamp: Date | string;
  description: string;
}

// Event types
export type EventType = 'input' | 'click' | 'success' | 'error' | 'view' | 'custom' | 'swipe' | 'scroll';

export interface EventParam {
  key: string;
  value: string | number | boolean;
  type: 'string' | 'number' | 'boolean';
}

export interface Comment {
  id: string;
  author: string;
  authorId: string; // User ID for avatar display
  authorRole: UserRole; // Global role of the user when they created the comment
  projectRole?: 'analyst' | 'developer' | 'viewer'; // Role in the project (for shared projects)
  text: string;
  createdAt: string;
}

// Event status - shows if event was added/removed/changed in this version
// Статуси подій:
// new = нова подія (зелений)
// edited = відредагована (жовтий)
// implemented = впроваджена (синій)
// pending_deletion = на видалення (червоний)
// deleted = видалена (сірий)
export type EventStatus = 'new' | 'edited' | 'implemented' | 'pending_deletion' | 'deleted';

// Status info with Ukrainian labels and colors
export const EVENT_STATUS_INFO: Record<EventStatus, { label: string; color: string }> = {
  new: { label: 'Нова', color: '#22c55e' }, // green-500
  edited: { label: 'Відредагована', color: '#eab308' }, // yellow-500
  implemented: { label: 'Впроваджена', color: '#3b82f6' }, // blue-500
  pending_deletion: { label: 'На видалення', color: '#ef4444' }, // red-500
  deleted: { label: 'Видалена', color: '#6b7280' }, // gray-500
};

export interface EventPreviousValues {
  name?: string;
  type?: EventType;
  description?: string;
  params?: EventParam[];
  changedAt: string;
  changedBy: string;
  changedById?: string; // User ID for avatar display
}

export interface AnalyticsEvent {
  id: string;
  name: string;
  type: EventType;
  description?: string; // optional field
  x: number; // percentage
  y: number; // percentage
  params: EventParam[];
  comments: Comment[];
  status: EventStatus;
  previousVersionId?: string; // link to event in previous version if changed
  implemented?: boolean; // marked as implemented in code (developer only)
  lastStatusChangedBy?: UserRole; // role of user who last changed status
  previousValues?: EventPreviousValues; // предыдущие значения при редактировании
  commentsReadAt?: string; // timestamp when analyst last read comments
  previousStatus?: EventStatus; // предыдущий статус для возврата (для разработчика)
}

// Screen with mockup
export interface Screen {
  id: string;
  name: string;
  displayName: string;
  imageUrl?: string;
  events: AnalyticsEvent[];
  markerScale?: number; // multiplier for marker size (default 1)
  popupScale?: number; // multiplier for popup size (default 1)
}

// App version - each app can have multiple versions
export interface AppVersion {
  id: string;
  version: string; // e.g., "1.0.0", "2.0.0"
  name: string; // e.g., "Production", "Beta"
  releaseDate: string;
  screens: Screen[];
  isActive: boolean;
  eventCount: number; // Кэшированный счётчик событий для проверки лимитов в Firestore Rules
}

// Member status for invitation flow
export type MemberStatus = 'pending' | 'active' | 'declined';

// Member info with role and invitation status
export interface MemberInfo {
  role: string;
  addedAt: string;
  joinedAt?: string; // For demo projects - when user joined the project
  email?: string;
  name?: string;
  status?: MemberStatus; // 'pending' - waiting for confirmation, 'active' - confirmed, 'declined' - declined
  invitedBy?: string; // userId who invited
  invitedAt?: string; // when invitation was sent
  confirmedAt?: string; // when invitation was accepted
}

// App - container for multiple versions
export interface App {
  id: string;
  name: string;
  description: string;
  icon?: string;
  createdAt: string;
  updatedAt: string;
  versions: AppVersion[];
  tasks?: Task[];
  ownerId: string;
  members?: Record<string, MemberInfo>;
  isDemo?: boolean;
  isOnboarding?: boolean;
  demoForUserId?: string;
}

// Task type for project tasks
export interface Task {
  id: string;
  title: string;
  description: string;
  author: string;
  createdAt: string;
  versions?: string[];
  status?: 'pending' | 'in_progress' | 'completed';
}

// Firestore user structure for authentication
export interface FirestoreUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: Date | string;
  createdBy: string; // ID of the analyst who created this user
}

// DEV mode test users (only used in development)
export const DEV_TEST_USERS: Record<string, { role: UserRole; name: string }> = {
  'analyst': { role: 'analyst', name: 'Аналітик (DEV)' },
  'developer': { role: 'developer', name: 'Розробник (DEV)' },
  'viewer': { role: 'viewer', name: 'Глядач (DEV)' },
};

// Event type labels
export const EVENT_TYPE_INFO: Record<EventType, { label: string; icon: string; color: string }> = {
  input: { label: 'Ввід', icon: '📝', color: '#3b82f6' },
  click: { label: 'Клік', icon: '👆', color: '#22c55e' },
  success: { label: 'Успіх', icon: '✅', color: '#10b981' },
  error: { label: 'Помилка', icon: '❌', color: '#ef4444' },
  view: { label: 'Перегляд', icon: '👁️', color: '#8b5cf6' },
  custom: { label: 'Інше', icon: '⚙️', color: '#f59e0b' },
  swipe: { label: 'Свайп', icon: '👉', color: '#06b6d4' },
  scroll: { label: 'Скрол', icon: '📜', color: '#84cc16' },
};

// Code generation format for Firebase Analytics - Dart/Flutter only
export type CodeFormat = 'dart';

export const CODE_FORMAT_INFO: Record<CodeFormat, { label: string; description: string }> = {
  dart: { label: 'Dart/Flutter', description: 'logEvent(name: ..., parameters: {...})' },
};

// Version Diff types
export interface EventChange {
  field: string;
  oldValue: any;
  newValue: any;
}

export interface AddedEvent {
  screenName: string;
  screenDisplayName: string;
  event: AnalyticsEvent;
}

export interface RemovedEvent {
  screenName: string;
  screenDisplayName: string;
  event: AnalyticsEvent;
}

export interface ChangedEvent {
  screenName: string;
  screenDisplayName: string;
  oldEvent: AnalyticsEvent;
  newEvent: AnalyticsEvent;
  changes: EventChange[];
}

export interface UnchangedEvent {
  screenName: string;
  screenDisplayName: string;
  event: AnalyticsEvent;
}

export interface AddedScreen {
  screen: Screen;
}

export interface RemovedScreen {
  screen: Screen;
}

export interface ChangedScreen {
  oldScreen: Screen;
  newScreen: Screen;
}

// Status change between versions
export interface StatusChange {
  screenName: string;
  screenDisplayName: string;
  eventName: string;
  eventId: string;
  baseStatus: EventStatus;
  targetStatus: EventStatus;
}

// Events grouped by status in target version
export interface EventsByStatus {
  pendingEvents: AddedEvent[]; // status = 'pending'
  inProgressEvents: AddedEvent[]; // status = 'in_progress'
  resolvedEvents: AddedEvent[]; // status = 'resolved'
  rejectedEvents: AddedEvent[]; // status = 'rejected'
}

export interface VersionDiff {
  baseVersionId: string;
  targetVersionId: string;
  baseVersionName: string;
  targetVersionName: string;
  // Screens
  addedScreens: AddedScreen[];
  removedScreens: RemovedScreen[];
  changedScreens: ChangedScreen[];
  // Events (by existence)
  addedEvents: AddedEvent[];
  removedEvents: RemovedEvent[];
  changedEvents: ChangedEvent[];
  unchangedEvents: UnchangedEvent[];
  // Status changes (for events that exist in both versions)
  statusChanges: StatusChange[];
  // Events grouped by status in target version
  eventsByStatus: EventsByStatus;
  stats: {
    totalAdded: number;
    totalRemoved: number;
    totalChanged: number;
    totalUnchanged: number;
    // Status stats
    totalPending: number;
    totalInProgress: number;
    totalResolved: number;
    totalRejected: number;
  };
}

// Event Filters for search and filtering
export interface EventFilters {
  search: string;
  statuses: EventStatus[];
  types: EventType[];
  screenId: string | null;
}

// Default filters (show all)
export const DEFAULT_EVENT_FILTERS: EventFilters = {
  search: '',
  statuses: [], // Empty means all statuses
  types: [], // Empty means all types
  screenId: null, // null means all screens
};

// Search result item
export interface SearchResultItem {
  event: AnalyticsEvent;
  screenId: string;
  screenName: string;
  screenDisplayName: string;
  matchedText: string; // Portion of text that matched
}

// Extended search result item for global search across all versions
export interface GlobalSearchResultItem {
  event: AnalyticsEvent;
  screenId: string;
  screenName: string;
  screenDisplayName: string;
  versionId: string;
  versionName: string;
  versionNumber: string;
  matchedText: string;
  matchedField: 'name' | 'description' | 'type' | 'param_key' | 'param_value';
}

// ============================================
// IMPORT DATA TYPES
// ============================================

// Для импортируемых данных
export interface ImportData {
  version?: string;
  apps?: ImportedApp[];
  screens?: ImportedScreen[];
  events?: ImportedEvent[];
  scope?: 'all' | 'app' | 'version' | 'screen';
  exportedAt?: string;
}

export interface ImportedApp {
  id?: string;
  name: string;
  description?: string;
  icon?: string;
  createdAt?: string;
  updatedAt?: string;
  versions?: ImportedVersion[];
}

export interface ImportedVersion {
  id?: string;
  version: string;
  releaseDate?: string;
  isActive?: boolean;
  screens?: ImportedScreen[];
}

export interface ImportedScreen {
  id?: string;
  name: string;
  displayName: string;
  imageUrl?: string;
  markerScale?: number;
  popupScale?: number;
  events?: ImportedEvent[];
}

export interface ImportedEvent {
  id?: string;
  name: string;
  type: EventType;
  description?: string;
  x: number;
  y: number;
  params?: EventParam[];
  comments?: Comment[];
  status?: EventStatus;
}

// ============================================
// HISTORY VALUE TYPES
// ============================================

// Для истории - старые и новые значения
export interface HistoryOldValue {
  name?: string;
  description?: string;
  x?: number;
  y?: number;
  status?: EventStatus;
  type?: EventType;
  params?: EventParam[];
  deleted?: boolean;
}

export interface HistoryNewValue {
  name?: string;
  description?: string;
  x?: number;
  y?: number;
  status?: EventStatus;
  type?: EventType;
  params?: EventParam[];
  text?: string;
}

// ============================================
// NOTIFICATION TYPES
// ============================================

export type NotificationType = 'success' | 'error' | 'info';

export interface Notification {
  type: NotificationType;
  message: string;
}

// ============================================
// DATA VERSION FOR MIGRATIONS
// ============================================

export interface PersistedState {
  _version?: number;
  apps?: App[];
  history?: HistoryRecord[];
  user?: User | null;
  sessionExpiry?: number | null;
  currentAppId?: string | null;
  currentVersionId?: string | null;
  currentScreenId?: string | null;
  eventFilters?: EventFilters;
  canvasZoom?: number;
  canvasPan?: { x: number; y: number };
}

// Re-export onboarding types
export * from './onboarding';

// Re-export image processing types
export * from './imageProcessing';
